import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, ExternalLink, ChevronRight } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot } from 'recharts';
import { fetchHomepageStats, fetchBreakdown } from '../config/api';
import SeoHead from '../seo/SeoHead';
import { homepageMeta } from '../seo/metadata';
import { organizationSchema, breadcrumbSchema } from '../seo/structuredData';
import { trackAlertSignup } from '../seo/analytics';

const INTER_STACK = "'Inter', system-ui, -apple-system, sans-serif";

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

const fallbackReports = [
  { id: 1, publication_date: '2026-03-13', report_title: 'Babcock & Wilcox Enterprises', short_seller: 'Wolfpack Research', link: '#' },
  { id: 2, publication_date: '2026-03-11', report_title: 'DI Early Signals Report', short_seller: 'Disclosure Insight', link: '#' },
  { id: 3, publication_date: '2026-03-10', report_title: 'Civeo Corporation', short_seller: 'Guasty Winds', link: '#' },
];

// Public, hand-tracked Diligent benchmark counts (see footnote on chart for
// methodology). Used purely as a visual comparison — Phalanx's archive is
// materially larger and more complete.
const DILIGENT_YEARLY = {
  2010: 36,
  2011: 83,
  2012: 74,
  2013: 157,
  2014: 190,
  2015: 279,
  2016: 264,
  2017: 191,
  2018: 160,
  2019: 170,
  2020: 156,
  2021: 126,
  2022: 96,
  2023: 110,
  2024: 107,
  2025: 166
};

const CountUpNumber = ({ end, duration = 600, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated && end > 0) {
          setHasAnimated(true);
          setCount(end);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, hasAnimated]);

  return (
    <span 
      ref={ref} 
      className="inline-block transition-all duration-500"
      style={{ opacity: hasAnimated ? 1 : 0, transform: hasAnimated ? 'translateY(0)' : 'translateY(8px)' }}
    >
      {count.toLocaleString()}{suffix}
    </span>
  );
};

// Coerce arbitrary API values (strings, numbers, null) into a positive integer.
// Returns null when the value can't be resolved so callers can keep their fallback.
const safePositiveInt = (value) => {
  if (value === null || value === undefined) return null;
  const n = typeof value === 'number' ? value : parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const YEARS_OF_COVERAGE = 30;

// The reports/companies readout shared by rows and sub-rows.
const CountReadout = ({ reports, companies, small }) => (
  <span style={{ fontFamily: INTER_STACK, fontSize: small ? '0.75rem' : '0.8rem', whiteSpace: 'nowrap' }}>
    <span className="text-gray-900 font-medium">{reports.toLocaleString()}</span>
    <span className="text-gray-300"> · </span>
    <span className="text-gray-500">{companies.toLocaleString()}</span>
  </span>
);

// The four dimensions each Coverage panel can pivot by.
const DIMENSIONS = [
  { key: 'region', label: 'Region' },
  { key: 'country', label: 'Country' },
  { key: 'exchange', label: 'Exchange' },
  { key: 'sector', label: 'Sector' },
];
const dimLabel = (key) => (DIMENSIONS.find((d) => d.key === key) || {}).label || key;

// Groups flat fact rows into a 2-level tree by the chosen top/second dimension.
// Every node's totals are the sum of its children, so numbers add up at both
// levels. (A company that spans two cells is a rare edge that slightly inflates
// company counts — reports are always exact.)
function pivotFacts(facts, topKey, secondKey) {
  const top = new Map();
  for (const f of facts) {
    const k1 = f[topKey];
    const k2 = f[secondKey];
    if (!top.has(k1)) top.set(k1, { name: k1, reports: 0, companies: 0, kids: new Map() });
    const t = top.get(k1);
    t.reports += f.reports;
    t.companies += f.companies;
    if (!t.kids.has(k2)) t.kids.set(k2, { name: k2, reports: 0, companies: 0 });
    const s = t.kids.get(k2);
    s.reports += f.reports;
    s.companies += f.companies;
  }
  return Array.from(top.values())
    .map((t) => ({
      name: t.name,
      reports: t.reports,
      companies: t.companies,
      children: Array.from(t.kids.values()).sort((a, b) => b.reports - a.reports),
    }))
    .sort((a, b) => b.reports - a.reports);
}

// A configurable breakdown panel. Draggable chips set the pivot order; the
// first two chips are the active top / second levels. Rows expand to reveal
// the second level as a proportional-bar drill-down.
const PivotPanel = ({ facts, defaultOrder }) => {
  const [order, setOrder] = useState(defaultOrder);
  const [open, setOpen] = useState(() => new Set());
  const [overIndex, setOverIndex] = useState(null);
  const dragIndex = useRef(null);
  const didDrop = useRef(false);

  const [topKey, secondKey] = order;
  const tree = useMemo(() => pivotFacts(facts, topKey, secondKey), [facts, topKey, secondKey]);
  const maxReports = Math.max(1, ...tree.map((t) => t.reports));

  const toggle = (name) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const reorder = (from, to) => {
    if (from == null || from === to) return;
    setOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setOpen(new Set());
  };

  // A plain click (no drag) promotes a chip to the top level.
  const promote = (i) => {
    if (didDrop.current) { didDrop.current = false; return; }
    reorder(i, 0);
  };

  return (
    <div className="bg-white p-5 sm:p-7 rounded-xl border border-gray-200">
      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        {order.map((key, i) => {
          const active = i < 2;
          return (
            <React.Fragment key={key}>
              <button
                draggable
                onDragStart={() => { dragIndex.current = i; didDrop.current = false; }}
                onDragOver={(e) => { e.preventDefault(); setOverIndex(i); }}
                onDragLeave={() => setOverIndex((v) => (v === i ? null : v))}
                onDrop={() => { reorder(dragIndex.current, i); didDrop.current = true; dragIndex.current = null; setOverIndex(null); }}
                onDragEnd={() => { dragIndex.current = null; setOverIndex(null); }}
                onClick={() => promote(i)}
                title="Drag to reorder levels, or click to make it the top level"
                className={`flex items-center gap-1 rounded-full border px-2.5 py-1 cursor-grab active:cursor-grabbing transition-colors ${
                  active ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300'
                } ${overIndex === i ? 'ring-2 ring-gray-300' : ''}`}
                style={{ fontFamily: INTER_STACK, fontWeight: 500, fontSize: '0.72rem' }}
              >
                {active && <span style={{ opacity: 0.55 }}>{i + 1}</span>}
                {dimLabel(key)}
              </button>
              {i === 1 && <span className="mx-0.5 text-gray-300 select-none">|</span>}
            </React.Fragment>
          );
        })}
      </div>
      <div
        className="text-gray-400 mb-5"
        style={{ fontFamily: INTER_STACK, fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}
      >
        {dimLabel(topKey)} → {dimLabel(secondKey)} · drag chips to change · reports · companies
      </div>

      <div
        className={`space-y-3 ${tree.length > 6 ? 'overflow-y-auto pr-1 -mr-1' : ''}`}
        style={tree.length > 6 ? { maxHeight: '19rem' } : undefined}
      >
        {tree.map((item) => {
          const children = item.children || [];
          const isOpen = open.has(item.name);
          const maxChild = Math.max(1, ...children.map((c) => c.reports));
          return (
            <div key={item.name}>
              <div
                onClick={() => toggle(item.name)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(item.name); } }}
                className="cursor-pointer select-none rounded-md -mx-2 px-2 py-1 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-baseline justify-between mb-1.5">
                  <span className="flex items-center text-gray-700" style={{ fontFamily: INTER_STACK, fontWeight: 400, fontSize: '0.85rem' }}>
                    <ChevronRight
                      size={13}
                      className="text-gray-400 mr-1 transition-transform duration-300 shrink-0"
                      style={{ transform: isOpen ? 'rotate(90deg)' : 'none' }}
                    />
                    {item.name}
                  </span>
                  <CountReadout reports={item.reports} companies={item.companies} />
                </div>
                <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gray-900 transition-all duration-700"
                    style={{ width: `${(item.reports / maxReports) * 100}%` }}
                  />
                </div>
              </div>

              {isOpen && (
                <div className="mt-3 mb-1 ml-2 pl-4 border-l border-gray-200 space-y-2.5">
                  {children.map((c) => (
                    <div key={c.name}>
                      <div className="flex items-baseline justify-between mb-1">
                        <span className="text-gray-500" style={{ fontFamily: INTER_STACK, fontWeight: 400, fontSize: '0.8rem' }}>
                          {c.name}
                        </span>
                        <CountReadout reports={c.reports} companies={c.companies} small />
                      </div>
                      <div className="h-1 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gray-400 transition-all duration-500"
                          style={{ width: `${(c.reports / maxChild) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LandingPage = () => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_reports: 3500,
    total_activists: 82,
    latest_report: null,
    active_researchers: 50,
    inactive_researchers: 26,
    top_sectors: ['Information Technology', 'Health Care', 'Industrials']
  });
  const [ytdReports, setYtdReports] = useState(82);
  const [projectedReports, setProjectedReports] = useState(503);
  const [breakdown, setBreakdown] = useState({ facts: [] });
  const [latestReports, setLatestReports] = useState(fallbackReports);
  const [showPitchModal, setShowPitchModal] = useState(false);
  const [pitchPassword, setPitchPassword] = useState('');
  const [pitchError, setPitchError] = useState(false);
  const [activeAudience, setActiveAudience] = useState('hedge');

  const audienceContent = {
    hedge: {
      title: 'Hedge Fund Managers',
      body: 'Screen every activist thesis before it moves the market. We build custom data feeds and alerting systems tailored to your strategy: researcher track records, sector targeting patterns, and real-time campaign notifications.'
    },
    institutional: {
      title: 'Asset Managers',
      body: 'Stay informed when short campaigns target companies in your portfolio. We deliver custom monitoring solutions with alerts configured to your holdings and risk parameters.'
    },
    ir: {
      title: 'Investor Relations',
      body: 'Understand who might target your company and why. We provide tailored vulnerability assessments and researcher profiles customized to your sector and peer group.'
    },
    law: {
      title: 'Law Firms',
      body: 'Surface prior allegations, fraud claims, and management red flags. We build custom evidence packages and historical research trails to support investor litigation opportunities.'
    }
  };

  const handlePitchAccess = () => {
    if (pitchPassword === 'Luckin') {
      window.open('/phalanx-pitch.pdf', '_blank');
      setShowPitchModal(false);
      setPitchPassword('');
      setPitchError(false);
    } else {
      setPitchError(true);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const { summary: summaryData, yearly: yearlyData, latestReports: reportsData } =
          await fetchHomepageStats();

        if (reportsData && Array.isArray(reportsData) && reportsData.length > 0) {
          // Defensive client-side dedupe. The API already collapses real
          // article-level duplicates (same URL + date, e.g. HunterBrook's
          // 'BREAKING:' twins), but we mirror that guard here so a stale
          // CDN response or future scraper regression cannot surface as a
          // visible double entry in the table. We intentionally do NOT
          // dedupe by activist — two reports from the same author on
          // different days are both valid latest entries.
          const seen = new Set();
          const deduped = [];
          for (const r of reportsData) {
            const cleanedTitle = (r.report_title || '')
              .replace(/^breaking[:\s-]+/i, '')
              .trim()
              .toLowerCase();
            const key = [
              (r.link || '').trim() || `__no_link_${r.id}`,
              r.publication_date || '',
              cleanedTitle || (r.short_seller || '').toLowerCase()
            ].join('|');
            if (seen.has(key)) continue;
            seen.add(key);
            deduped.push(r);
          }
          setLatestReports(deduped);
        }

        if (summaryData) {
          setStats(prev => ({
            total_reports: safePositiveInt(summaryData.total_reports) ?? prev.total_reports,
            total_activists: safePositiveInt(summaryData.total_activists) ?? prev.total_activists,
            latest_report: summaryData.latest_report ?? prev.latest_report,
            active_researchers: safePositiveInt(summaryData.active_researchers) ?? prev.active_researchers,
            inactive_researchers: safePositiveInt(summaryData.inactive_researchers) ?? prev.inactive_researchers,
            top_sectors: Array.isArray(summaryData.top_sectors) && summaryData.top_sectors.length > 0
              ? summaryData.top_sectors
              : prev.top_sectors
          }));
        }
        
        if (yearlyData && Array.isArray(yearlyData)) {
          const now = new Date();
          const currentYear = now.getFullYear();
          const rawData = yearlyData.map(row => ({
            year: row.year.toString(),
            reports: parseInt(row.reports) || 0,
            activists: parseInt(row.activists) || 0,
            companies: parseInt(row.companies) || 0,
            diligent: DILIGENT_YEARLY[row.year] ?? null
          }));
          
          const currentYearData = rawData.find(d => d.year === currentYear.toString());
          const ytd = currentYearData?.reports || 0;
          
          const startOfYear = new Date(currentYear, 0, 1);
          const dayOfYear = Math.ceil((now - startOfYear) / (1000 * 60 * 60 * 24));
          const daysInYear = ((currentYear % 4 === 0 && currentYear % 100 !== 0) || currentYear % 400 === 0) ? 366 : 365;
          const projected = Math.round(ytd * (daysInYear / dayOfYear));
          
          setYtdReports(ytd);
          setProjectedReports(projected);
          
          const chartDataWithProjection = rawData.map(row => {
            if (row.year === currentYear.toString()) {
              return { ...row, reports: projected, ytdReports: ytd, isCurrentYear: true };
            }
            return { ...row, isCurrentYear: false };
          });
          
          setChartData(chartDataWithProjection);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        const currentYear = new Date().getFullYear();
        setChartData([
          { year: '2010', reports: 47, diligent: DILIGENT_YEARLY[2010], isCurrentYear: false },
          { year: '2011', reports: 94, diligent: DILIGENT_YEARLY[2011], isCurrentYear: false },
          { year: '2012', reports: 40, diligent: DILIGENT_YEARLY[2012], isCurrentYear: false },
          { year: '2013', reports: 65, diligent: DILIGENT_YEARLY[2013], isCurrentYear: false },
          { year: '2014', reports: 51, diligent: DILIGENT_YEARLY[2014], isCurrentYear: false },
          { year: '2015', reports: 83, diligent: DILIGENT_YEARLY[2015], isCurrentYear: false },
          { year: '2016', reports: 84, diligent: DILIGENT_YEARLY[2016], isCurrentYear: false },
          { year: '2017', reports: 165, diligent: DILIGENT_YEARLY[2017], isCurrentYear: false },
          { year: '2018', reports: 179, diligent: DILIGENT_YEARLY[2018], isCurrentYear: false },
          { year: '2019', reports: 241, diligent: DILIGENT_YEARLY[2019], isCurrentYear: false },
          { year: '2020', reports: 280, diligent: DILIGENT_YEARLY[2020], isCurrentYear: false },
          { year: '2021', reports: 217, diligent: DILIGENT_YEARLY[2021], isCurrentYear: false },
          { year: '2022', reports: 293, diligent: DILIGENT_YEARLY[2022], isCurrentYear: false },
          { year: '2023', reports: 351, diligent: DILIGENT_YEARLY[2023], isCurrentYear: false },
          { year: '2024', reports: 468, diligent: DILIGENT_YEARLY[2024], isCurrentYear: false },
          { year: '2025', reports: 644, diligent: DILIGENT_YEARLY[2025], isCurrentYear: false },
          { year: currentYear.toString(), reports: 513, ytdReports: 163, diligent: null, isCurrentYear: true }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Coverage breakdown (by sector / by region) — independent of hero stats so
  // a failure here never blocks the rest of the page.
  useEffect(() => {
    let cancelled = false;
    fetchBreakdown()
      .then((data) => {
        if (!cancelled) setBreakdown(data);
      })
      .catch((err) => console.error('Error fetching breakdown:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  // Shared heading style — matches the Northlight hero tagline treatment.
  const headingStyle = {
    fontFamily: INTER_STACK,
    fontWeight: 200,
    letterSpacing: '-0.02em'
  };
  const bodyStyle = {
    fontFamily: INTER_STACK,
    fontWeight: 300
  };
  // Small-caps CTA style (mirrors Northlight's "Get in touch" link).
  const ctaStyle = {
    fontFamily: INTER_STACK,
    fontWeight: 500,
    letterSpacing: '0.18em',
    fontSize: '0.7rem'
  };

  // Icons for the solution tiles — kept identical; classes set by parent for hover.
  const SolutionIcon = ({ path }) => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      {path}
    </svg>
  );

  const audienceTabs = [
    { id: 'hedge', label: 'Hedge Funds' },
    { id: 'institutional', label: 'Asset Managers' },
    { id: 'ir', label: 'Investor Relations' },
    { id: 'law', label: 'Law Firms' }
  ];

  const seo = homepageMeta({
    totalReports: stats.total_reports,
    totalActivists: stats.total_activists,
  });

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: INTER_STACK }}>
      <SeoHead
        {...seo}
        jsonLd={[
          organizationSchema(),
          breadcrumbSchema([{ name: 'Phalanx', path: '/' }]),
        ]}
      />
      {/* Header — transparent, floats over the hero. Phalanx wordmark in Playfair Display
          (the same font used on the Phalanx tile on the Northlight portfolio). */}
      <header className="absolute top-0 left-0 right-0 z-30 px-6">
        <div className="max-w-6xl mx-auto py-5 sm:py-7">
          <nav className="flex items-center justify-between gap-4">
            <div className="flex items-baseline gap-5 sm:gap-6">
              <Link
                to="/"
                className="text-white leading-none hover:text-white/80 transition-colors"
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontWeight: 400,
                  letterSpacing: '-0.01em',
                  fontSize: 'clamp(1.5rem, 3.6vw, 2.75rem)',
                  textShadow: '0 2px 20px rgba(0,0,0,0.3)'
                }}
              >
                Phalanx
              </Link>
            </div>
            <div
              className="flex items-center gap-4 sm:gap-6"
              style={{
                ...bodyStyle,
                fontSize: '0.85rem',
                letterSpacing: '-0.01em',
                textShadow: '0 2px 20px rgba(0,0,0,0.3)'
              }}
            >
              <a href="#growth" className="text-white/70 hover:text-white hidden sm:inline transition-colors">Growth</a>
              <Link to="/research" className="text-white/70 hover:text-white transition-colors">Research</Link>
              <button
                onClick={() => setShowPitchModal(true)}
                className="text-white/70 hover:text-white transition-colors"
              >
                Pitch
              </button>
              <Link to="/login" className="text-white hover:text-white/70 transition-colors">Login</Link>
            </div>
          </nav>
        </div>
      </header>

      {/* Hero — full viewport mountain image with the three headline stats overlaid at the bottom */}
      <section className="relative h-screen overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-bottom bg-no-repeat"
          style={{ backgroundImage: "url('/phalanx-bg.png')", backgroundSize: 'cover' }}
        />
        <div className="absolute inset-0 bg-black/40" />

        <div className="relative h-full flex flex-col">
          {/* Centered headline + copy + CTA */}
          <div className="flex-1 flex items-center justify-center px-6">
            <div className="text-center max-w-4xl">
              <h1
                className="text-white mb-6 leading-[1.15]"
                style={{
                  ...headingStyle,
                  fontSize: 'clamp(1.75rem, 4.2vw, 3rem)',
                  textShadow: '0 2px 20px rgba(0,0,0,0.3)'
                }}
              >
                Every Activist Short Campaign. One Database.
              </h1>
              <p
                className="text-white/85 leading-relaxed max-w-3xl mx-auto"
                style={{
                  ...bodyStyle,
                  fontSize: 'clamp(0.95rem, 1.6vw, 1.15rem)',
                  textShadow: '0 1px 12px rgba(0,0,0,0.3)'
                }}
              >
                Activist short sellers are publishing more than ever, emerging researchers appear daily, and veterans vanish without trace.
              </p>
              <p
                className="text-white/85 leading-relaxed max-w-3xl mx-auto mt-4"
                style={{
                  ...bodyStyle,
                  fontSize: 'clamp(0.95rem, 1.6vw, 1.15rem)',
                  textShadow: '0 1px 12px rgba(0,0,0,0.3)'
                }}
              >
                Phalanx is the only platform that captures every report and tracks every activist.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-white border-b border-white hover:border-gray-300 hover:text-gray-300 pb-0.5 uppercase transition-colors mt-8 sm:mt-10"
                style={ctaStyle}
              >
                Access Short Seller Data
                <span className="text-base">→</span>
              </Link>
            </div>
          </div>

          {/* Stats row — lifted higher into the mountain image so the numbers
              read as headline metrics, not a footer. */}
          <div className="pb-24 sm:pb-32 px-6">
            <div className="max-w-5xl mx-auto grid grid-cols-3 gap-3 sm:gap-8 text-center">
              {[
                {
                  value: loading ? '...' : <CountUpNumber end={stats.total_reports} />,
                  label: 'Reports Archived'
                },
                {
                  value: loading ? '...' : <CountUpNumber end={stats.total_activists} />,
                  label: 'Researchers Profiled'
                },
                {
                  value: loading ? '...' : <CountUpNumber end={YEARS_OF_COVERAGE} />,
                  label: 'Years of Coverage'
                }
              ].map((stat, i) => (
                <div key={i} className="px-2 sm:px-4">
                  <div
                    className="text-white mb-2 sm:mb-3"
                    style={{
                      ...headingStyle,
                      fontSize: 'clamp(1.95rem, 4.6vw, 3.5rem)',
                      textShadow: '0 2px 20px rgba(0,0,0,0.35)'
                    }}
                  >
                    {stat.value}
                  </div>
                  <div
                    className="text-white/75 uppercase"
                    style={{
                      ...bodyStyle,
                      fontSize: '0.72rem',
                      letterSpacing: '0.22em',
                      textShadow: '0 1px 12px rgba(0,0,0,0.3)'
                    }}
                  >
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Growth Chart Section */}
      <section id="growth" className="py-12 sm:py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6 sm:mb-8">
            <h2
              className="text-gray-900 mb-2 leading-[1.2]"
              style={{ ...headingStyle, fontSize: 'clamp(1.5rem, 2.8vw, 2rem)' }}
            >
              Activist Short Selling Has Grown Significantly
            </h2>
            <p className="text-gray-500" style={{ ...bodyStyle, fontSize: '0.85rem' }}>
              Reports published per year since 2000 — Phalanx vs Diligent.
            </p>
            <p
              className="text-gray-600 mt-3 leading-relaxed"
              style={{ ...bodyStyle, fontSize: '0.88rem' }}
            >
              Phalanx maintains a larger archive and significantly higher data quality than other leading data providers in activist short selling.
            </p>
          </div>

          <div className="bg-white p-5 sm:p-8 rounded-xl border border-gray-200">
            <div className="mb-4">
              <div className="flex items-baseline gap-4 sm:gap-6">
                <div>
                  <div
                    className="text-gray-900"
                    style={{ ...headingStyle, fontSize: 'clamp(1.5rem, 2.6vw, 2rem)' }}
                  >
                    {loading ? '...' : ytdReports.toLocaleString()}
                  </div>
                  <div className="text-gray-500 mt-1" style={{ ...bodyStyle, fontSize: '0.78rem' }}>
                    {new Date().getFullYear()} YTD
                  </div>
                </div>
                <div className="text-gray-200 text-2xl font-light">|</div>
                <div>
                  <div
                    className="text-gray-400"
                    style={{ ...headingStyle, fontSize: 'clamp(1.5rem, 2.6vw, 2rem)' }}
                  >
                    {loading ? '...' : projectedReports.toLocaleString()}
                  </div>
                  <div className="text-gray-400 mt-1" style={{ ...bodyStyle, fontSize: '0.78rem' }}>
                    Projected (Pro-rata)
                  </div>
                </div>
              </div>
              <div className="text-gray-500 mt-3" style={{ ...bodyStyle, fontSize: '0.8rem' }}>
                {loading ? '...' : stats.total_reports.toLocaleString()} total reports tracked
              </div>
            </div>

            <ResponsiveContainer width="100%" height={250}>
              {chartData.length > 0 ? (
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis
                    dataKey="year"
                    stroke="#9ca3af"
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    stroke="#9ca3af"
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      fontSize: '12px',
                      borderRadius: '8px'
                    }}
                    formatter={(value, name, props) => {
                      const data = props.payload;
                      if (value == null) return [null, null];
                      if (name === 'diligent') {
                        return [value.toLocaleString(), 'Diligent'];
                      }
                      if (data.isCurrentYear && name === 'reports') {
                        return [`${value.toLocaleString()} (Projected)`, 'Phalanx'];
                      }
                      return [value.toLocaleString(), name === 'reports' ? 'Phalanx' : name];
                    }}
                    labelFormatter={(label) => `Year: ${label}`}
                  />
                  <Line
                    type="monotone"
                    dataKey="diligent"
                    stroke="#9ca3af"
                    strokeWidth={1.5}
                    strokeDasharray="4 4"
                    dot={{ r: 2.5, fill: '#9ca3af', stroke: '#9ca3af' }}
                    activeDot={{ r: 4, stroke: '#9ca3af', strokeWidth: 1 }}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="reports"
                    stroke="#111827"
                    strokeWidth={2}
                    dot={(props) => {
                      const { cx, cy, payload } = props;
                      if (payload.isCurrentYear) {
                        return (
                          <circle
                            cx={cx}
                            cy={cy}
                            r={4}
                            fill="white"
                            stroke="#9ca3af"
                            strokeWidth={2}
                            strokeDasharray="3 3"
                          />
                        );
                      }
                      return <circle cx={cx} cy={cy} r={3} fill="#111827" />;
                    }}
                    activeDot={{ r: 5, stroke: '#111827', strokeWidth: 1 }}
                  />
                  {chartData.length > 0 && ytdReports > 0 && (
                    <ReferenceDot
                      x={new Date().getFullYear().toString()}
                      y={ytdReports}
                      r={6}
                      fill="#3b82f6"
                      stroke="white"
                      strokeWidth={2}
                    />
                  )}
                </LineChart>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-gray-400 text-sm">Loading chart...</div>
                </div>
              )}
            </ResponsiveContainer>

            {/* Legend */}
            <div
              className="mt-5 pt-5 border-t border-gray-100 flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3 sm:gap-6"
              style={{ ...bodyStyle, fontSize: '0.8rem' }}
            >
              <div className="flex items-center gap-2">
                <div className="w-4 sm:w-5 h-0.5 bg-gray-900"></div>
                <span className="text-gray-700">Phalanx</span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-4 sm:w-5 h-0 border-t border-gray-400"
                  style={{ borderStyle: 'dashed' }}
                ></div>
                <span className="text-gray-500">Diligent</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-blue-500"></div>
                <span className="text-gray-600">{new Date().getFullYear()} YTD ({ytdReports.toLocaleString()})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full border-2 border-gray-400 border-dashed"></div>
                <span className="text-gray-600">Projected ({projectedReports.toLocaleString()})</span>
              </div>
              {(() => {
                // Phalanx vs Diligent multiple, computed across the years where
                // Diligent data is available (2010–2025 when not YTD year).
                const overlap = chartData.filter(
                  d => d.diligent != null && !d.isCurrentYear
                );
                if (overlap.length === 0) return null;
                const phalanxSum = overlap.reduce((s, d) => s + (d.reports || 0), 0);
                const diligentSum = overlap.reduce((s, d) => s + (d.diligent || 0), 0);
                if (diligentSum === 0) return null;
                const multiple = (phalanxSum / diligentSum).toFixed(1);
                const startYear = overlap[0].year;
                const endYear = overlap[overlap.length - 1].year;
                return (
                  <div className="inline-flex items-center text-gray-700">
                    <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 text-gray-900" />
                    <span className="text-gray-900 font-medium">{multiple}× more reports</span>
                    <span className="text-gray-500 ml-1">than Diligent ({startYear}–{endYear})</span>
                  </div>
                );
              })()}
            </div>
            <p
              className="mt-4 text-gray-400 leading-relaxed"
              style={{ ...bodyStyle, fontSize: '0.7rem' }}
            >
              Diligent figures are based on their Shareholder Activism Reviews and academic papers (UCLA, Boston University, and American Accounting Association).
            </p>
          </div>
        </div>
      </section>

      {/* Coverage Explorer Section — two configurable pivot panels */}
      {breakdown.facts.length > 0 && (
        <section id="coverage" className="py-12 sm:py-20 px-6 bg-gray-50 border-t border-gray-100">
          <div className="max-w-5xl mx-auto">
            <div className="mb-6 sm:mb-8">
              <h2
                className="text-gray-900 mb-2 leading-[1.2]"
                style={{ ...headingStyle, fontSize: 'clamp(1.5rem, 2.8vw, 2rem)' }}
              >
                Coverage Explorer
              </h2>
              <p className="text-gray-500" style={{ ...bodyStyle, fontSize: '0.85rem' }}>
                Reports tracked and distinct companies targeted. Drag the chips to pivot each panel by any two of region, country, exchange, or sector — then click a row to drill in.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-5 sm:gap-6">
              <PivotPanel facts={breakdown.facts} defaultOrder={['exchange', 'sector', 'region', 'country']} />
              <PivotPanel facts={breakdown.facts} defaultOrder={['region', 'country', 'exchange', 'sector']} />
            </div>
          </div>
        </section>
      )}

      {/* Problem Section */}
      <section className="py-20 sm:py-28 px-6 bg-gray-900 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2
            className="mb-6 leading-[1.15]"
            style={{ ...headingStyle, fontSize: 'clamp(1.75rem, 3.8vw, 2.75rem)' }}
          >
            The highest quality data on activist short selling.
          </h2>
          <p
            className="text-gray-300 leading-relaxed"
            style={{ ...bodyStyle, fontSize: 'clamp(0.95rem, 1.5vw, 1.1rem)' }}
          >
            Our archive includes reports that can no longer be found online, deleted by activists after their campaigns concluded. We are also the first to identify new activist short sellers, emerging players, and one-off researchers behind a single high-conviction target.
          </p>
        </div>
      </section>

      {/* Solution Section — four tiles */}
      <section className="py-14 sm:py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10 sm:mb-14">
            <p
              className="text-gray-400 mb-3 uppercase"
              style={{ ...bodyStyle, fontWeight: 500, fontSize: '0.68rem', letterSpacing: '0.26em' }}
            >
              The Solution
            </p>
            <h2
              className="text-gray-900 leading-[1.2]"
              style={{ ...headingStyle, fontSize: 'clamp(1.5rem, 2.8vw, 2rem)' }}
            >
              A structured archive built for short seller research
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {[
              {
                icon: <SolutionIcon path={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />} />,
                title: 'Machine-readable',
                body: 'Structured data for AI teams training models on fraud signals or campaign outcomes.'
              },
              {
                icon: <SolutionIcon path={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />} />,
                title: `${YEARS_OF_COVERAGE} years of history`,
                body: 'Track how the space evolved: sectors targeted, credibility scores, campaign cycles.'
              },
              {
                icon: <SolutionIcon path={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />} />,
                title: 'Real-time alerts',
                body: 'New reports captured instantly with notifications straight to trading systems and litigation pitches.'
              },
              {
                icon: <SolutionIcon path={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />} />,
                title: 'Permanent archive',
                body: 'Complete historical records preserved, including 15 researchers no longer in the spotlight.'
              }
            ].map((tile, i) => (
              <div
                key={i}
                className="group rounded-xl border border-gray-200 bg-[#FAF8F3] hover:bg-gray-900 hover:border-gray-900 p-6 sm:p-7 cursor-pointer"
                style={{ transition: 'background-color 700ms cubic-bezier(0.22, 1, 0.36, 1), border-color 700ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 700ms cubic-bezier(0.22, 1, 0.36, 1)' }}
              >
                <div
                  className="w-11 h-11 mb-5 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-700 group-hover:bg-gray-800 group-hover:border-gray-700 group-hover:text-white"
                  style={{ transition: 'background-color 700ms cubic-bezier(0.22, 1, 0.36, 1), border-color 700ms cubic-bezier(0.22, 1, 0.36, 1), color 700ms cubic-bezier(0.22, 1, 0.36, 1)' }}
                >
                  {tile.icon}
                </div>
                <h3
                  className="text-gray-900 group-hover:text-white mb-2 leading-snug"
                  style={{
                    ...headingStyle,
                    fontWeight: 400,
                    fontSize: '1.05rem',
                    letterSpacing: '-0.01em',
                    transition: 'color 700ms cubic-bezier(0.22, 1, 0.36, 1)'
                  }}
                >
                  {tile.title}
                </h3>
                <p
                  className="text-gray-600 group-hover:text-gray-300 leading-relaxed"
                  style={{
                    ...bodyStyle,
                    fontSize: '0.875rem',
                    transition: 'color 700ms cubic-bezier(0.22, 1, 0.36, 1)'
                  }}
                >
                  {tile.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Uses Phalanx Section */}
      <section className="py-14 sm:py-20 px-6 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2
              className="text-gray-900 leading-[1.2]"
              style={{ ...headingStyle, fontSize: 'clamp(1.5rem, 2.8vw, 2rem)' }}
            >
              Who uses Phalanx?
            </h2>
            <p className="text-gray-500 mt-2" style={{ ...bodyStyle, fontSize: '0.85rem' }}>
              We build custom data solutions for each client
            </p>
          </div>

          {/* Tabs */}
          <div className="flex justify-center border-b border-gray-200 mb-10 overflow-x-auto">
            {audienceTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveAudience(tab.id)}
                className={`px-4 sm:px-6 py-3 border-b-2 transition-colors whitespace-nowrap ${
                  activeAudience === tab.id
                    ? 'border-gray-900 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                style={{ ...bodyStyle, fontSize: '0.85rem', letterSpacing: '-0.005em' }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content — tile */}
          <div className="rounded-xl bg-white border border-gray-200 p-6 sm:p-10 text-center min-h-[180px]">
            <h3
              className="text-gray-900 mb-4"
              style={{ ...headingStyle, fontWeight: 400, fontSize: '1.1rem', letterSpacing: '-0.01em' }}
            >
              {audienceContent[activeAudience].title}
            </h3>
            <p
              className="text-gray-600 leading-relaxed max-w-2xl mx-auto"
              style={{ ...bodyStyle, fontSize: '0.95rem' }}
            >
              {audienceContent[activeAudience].body}
            </p>
          </div>
        </div>
      </section>

      {/* Latest Reports Table */}
      <section id="latest" className="py-14 sm:py-20 px-6 bg-white border-t border-gray-200">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <h2
                className="text-gray-900 leading-[1.2]"
                style={{ ...headingStyle, fontSize: 'clamp(1.5rem, 2.8vw, 2rem)' }}
              >
                Latest Reports
              </h2>
              <span
                className="text-gray-400 bg-gray-100 px-2 py-0.5 rounded"
                style={{ ...bodyStyle, fontSize: '0.68rem' }}
              >
                24hr lag
              </span>
            </div>
            <p className="text-gray-500" style={{ ...bodyStyle, fontSize: '0.85rem' }}>
              Most recent activist short seller publications
            </p>
          </div>

          <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-gray-500 uppercase w-24" style={{ ...bodyStyle, fontSize: '0.68rem', letterSpacing: '0.14em', fontWeight: 500 }}>Date</th>
                    <th className="px-4 py-3 text-left text-gray-500 uppercase" style={{ ...bodyStyle, fontSize: '0.68rem', letterSpacing: '0.14em', fontWeight: 500 }}>Report</th>
                    <th className="px-4 py-3 text-left text-gray-500 uppercase w-40" style={{ ...bodyStyle, fontSize: '0.68rem', letterSpacing: '0.14em', fontWeight: 500 }}>Activist</th>
                    <th className="px-4 py-3 text-left text-gray-500 uppercase w-16" style={{ ...bodyStyle, fontSize: '0.68rem', letterSpacing: '0.14em', fontWeight: 500 }}></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {latestReports.map((report, index) => (
                    <tr key={report.id || index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap" style={{ ...bodyStyle, fontSize: '0.85rem' }}>
                        {formatDate(report.publication_date || report.report_date)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-900 line-clamp-1" style={{ ...bodyStyle, fontSize: '0.9rem', fontWeight: 400 }}>
                          {report.report_title || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap" style={{ ...bodyStyle, fontSize: '0.85rem' }}>
                        {report.short_seller || report.activist_short_seller || '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {report.link || report.report_url ? (
                          <a
                            href={report.link || report.report_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-900 transition-colors"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-14 sm:py-20 px-6 bg-gray-900 text-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-10 items-center">
            <div>
              <h3
                className="mb-4 leading-[1.2]"
                style={{ ...headingStyle, fontSize: 'clamp(1.5rem, 2.8vw, 2rem)' }}
              >
                Stay Ahead of Short Campaigns
              </h3>
              <p
                className="text-gray-400 mb-5 leading-relaxed"
                style={{ ...bodyStyle, fontSize: '0.9rem' }}
              >
                Get notified when new activist short sellers emerge and receive summaries of their latest targets. Be the first to know about potential market-moving reports.
              </p>
              <ul className="text-gray-400 space-y-2.5" style={{ ...bodyStyle, fontSize: '0.875rem' }}>
                <li className="flex items-center gap-2.5">
                  <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  New activist short seller alerts
                </li>
                <li className="flex items-center gap-2.5">
                  <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Latest target company notifications
                </li>
                <li className="flex items-center gap-2.5">
                  <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  AI-generated report summaries
                </li>
              </ul>
            </div>
            <div className="bg-gray-800/60 rounded-xl border border-gray-700 p-6 sm:p-8 text-center">
              <div className="mb-4">
                <svg className="w-10 h-10 text-white mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h4
                className="mb-2"
                style={{ ...headingStyle, fontWeight: 400, fontSize: '1.05rem' }}
              >
                Get the Latest Research
              </h4>
              <p
                className="text-gray-400 mb-5 leading-relaxed"
                style={{ ...bodyStyle, fontSize: '0.85rem' }}
              >
                Email us to be onboarded for the latest activist short seller research and notifications.
              </p>
              <a
                href="mailto:Contact@researchphalanx.com?subject=Phalanx Research Alerts - Onboarding Request"
                onClick={() => trackAlertSignup({
                  source: 'landing_page',
                  subject: 'Phalanx Research Alerts - Onboarding Request',
                })}
                className="inline-flex items-center justify-center gap-2 w-full px-4 py-3 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
                style={{ ...bodyStyle, fontWeight: 500, fontSize: '0.875rem' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Contact Us to Subscribe
              </a>
              <p className="text-gray-500 mt-3" style={{ ...bodyStyle, fontSize: '0.7rem' }}>
                Contact@researchphalanx.com
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-14 sm:py-20 px-6 bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto text-center">
          <h3
            className="text-gray-900 mb-4 leading-[1.2]"
            style={{ ...headingStyle, fontSize: 'clamp(1.5rem, 2.8vw, 2rem)' }}
          >
            Ready to get started?
          </h3>
          <p
            className="text-gray-600 mb-8 sm:mb-10"
            style={{ ...bodyStyle, fontSize: '0.9rem' }}
          >
            Get in touch to learn how Phalanx can support your research.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-gray-900 border-b border-gray-900 hover:border-gray-500 hover:text-gray-500 pb-0.5 uppercase transition-colors"
            style={ctaStyle}
          >
            Access Short Seller Data
            <span className="text-base">→</span>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 px-6">
        <div
          className="max-w-5xl mx-auto py-6 sm:py-8 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4"
          style={{ ...bodyStyle, fontSize: '0.8rem' }}
        >
          <span
            className="text-gray-900 leading-none"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontWeight: 400,
              letterSpacing: '-0.01em',
              fontSize: '1.25rem'
            }}
          >
            Phalanx
          </span>
          <p className="text-gray-400">&copy; 2026 Phalanx. A Northlight Technologies company.</p>
        </div>
      </footer>

      {/* Pitch Materials Password Modal */}
      {showPitchModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 sm:p-7" style={{ fontFamily: INTER_STACK }}>
            <div className="flex justify-between items-center mb-5">
              <h3
                className="text-gray-900"
                style={{ ...headingStyle, fontWeight: 400, fontSize: '1.1rem', letterSpacing: '-0.01em' }}
              >
                Pitch Materials
              </h3>
              <button
                onClick={() => { setShowPitchModal(false); setPitchPassword(''); setPitchError(false); }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-gray-500 mb-4" style={{ ...bodyStyle, fontSize: '0.85rem' }}>
              Enter password to access pitch materials.
            </p>
            <input
              type="password"
              value={pitchPassword}
              onChange={(e) => { setPitchPassword(e.target.value); setPitchError(false); }}
              onKeyDown={(e) => e.key === 'Enter' && handlePitchAccess()}
              placeholder="Password"
              className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${
                pitchError ? 'border-red-500' : 'border-gray-300'
              }`}
              style={{ ...bodyStyle, fontSize: '0.9rem' }}
              autoFocus
            />
            {pitchError && (
              <p className="text-red-500 mt-1.5" style={{ ...bodyStyle, fontSize: '0.75rem' }}>Incorrect password</p>
            )}
            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={() => { setShowPitchModal(false); setPitchPassword(''); setPitchError(false); }}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
                style={{ ...bodyStyle, fontSize: '0.85rem' }}
              >
                Cancel
              </button>
              <button
                onClick={handlePitchAccess}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                style={{ ...bodyStyle, fontWeight: 500, fontSize: '0.85rem' }}
              >
                Access
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
